import React, { useEffect, useMemo, useState } from 'react';
import { Prism as SyntaxHighlighterBase } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import type { AppSettings } from '../utils/SettingsManager';
import { message } from 'antd';

type CodeProps = {
    inline?: boolean;
    className?: string;
    children?: React.ReactNode;
    settings: AppSettings;
} & React.HTMLAttributes<HTMLElement>;

// 安全地提取 children 文本内容
const toText = (ch: any): string => {
    if (ch == null) return '';
    if (typeof ch === 'string' || typeof ch === 'number') return String(ch);
    if (Array.isArray(ch)) return ch.map(toText).join('');
    if (typeof ch === 'object' && ch.props && 'children' in ch.props) {
        return toText(ch.props.children);
    }
    try {
        return String(ch);
    } catch {
        return '';
    }
};

const CodeBlock: React.FC<CodeProps> = ({ inline, className, children, settings, ...rest }) => {
    const [copied, setCopied] = useState(false);
    const original = useMemo(() => toText(children).replace(/\n$/, ''), [children]);
    const [displayed, setDisplayed] = useState(original);
    const [formatting, setFormatting] = useState(false);

    useEffect(() => {
        setDisplayed(original);
    }, [original]);

    if (inline) {
        return (
            <code
                className={className}
                {...rest}
                style={{
                    background: settings.darkMode ? '#2d2d2d' : '#f6f8fa',
                    color: settings.darkMode ? '#fff' : '#262626',
                    padding: '2px 4px',
                    borderRadius: 3,
                    fontSize: '0.9em',
                }}
            >
                {displayed}
            </code>
        );
    }

    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : undefined;

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(displayed);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            // 回退方案
            try {
                const textarea = document.createElement('textarea');
                textarea.value = displayed;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            } catch { }
        }
    };

    // 语言到 Prettier 解析器的映射
    const parser = useMemo((): string | undefined => {
        if (!language) return undefined;
        const lang = language.toLowerCase();
        if (['js', 'javascript', 'jsx', 'mjs', 'cjs'].includes(lang)) return 'babel';
        if (['ts', 'tsx', 'typescript'].includes(lang)) return 'typescript';
        if (['json', 'json5', 'jsonc'].includes(lang)) return 'json';
        if (['html', 'xml'].includes(lang)) return 'html';
        if (['css', 'scss', 'less'].includes(lang)) return 'css';
        if (['md', 'markdown'].includes(lang)) return 'markdown';
        if (['yml', 'yaml'].includes(lang)) return 'yaml';
        if (['gql', 'graphql'].includes(lang)) return 'graphql';
        if (['java'].includes(lang)) return 'java';
        if (['c', 'h', 'hpp', 'hh', 'hxx', 'c++', 'cpp', 'cc', 'cxx'].includes(lang)) return 'c-like';
        if (['py', 'python'].includes(lang)) return 'python-simple';
        return undefined;
    }, [language]);

    const onFormat = async () => {
        if (!parser) {
            message.info('当前语言暂不支持前端一键格式化');
            return;
        }
        setFormatting(true);
        try {
            if (parser === 'c-like') {
                // 轻量级 C/C++/C 格式化（缩进对齐 + 去除多余空白）——非完整 clang-format，仅临时方案
                const indentSize = 2;
                let indent = 0;
                const formatted = displayed
                    .replace(/\t/g, '    ')
                    .split(/\r?\n/)
                    .map(rawLine => {
                        let line = rawLine.trimEnd();
                        const trimmed = line.trim();
                        if (!trimmed) return '';
                        // 先判断是否需要减少缩进
                        const dedentFirst = /^(}\s*;?|}\)|}\]|\}\s*else|case |default:)/.test(trimmed);
                        if (dedentFirst && indent > 0) indent--;
                        const currentIndent = ' '.repeat(indent * indentSize);
                        // 统计本行出现的 { 与 }
                        let open = (trimmed.match(/\{/g) || []).length;
                        let close = (trimmed.match(/\}/g) || []).length;
                        // 对称处理可能的同一行 { }
                        if (open === close && /{[^}]*}/.test(trimmed)) {
                            // 不改变 indent
                        } else {
                            indent += open - close;
                            if (indent < 0) indent = 0;
                        }
                        return currentIndent + trimmed.replace(/\s+$/, '');
                    })
                    .join('\n')
                    .replace(/\n{3,}/g, '\n\n');
                setDisplayed(formatted.trimEnd());
            } else if (parser === 'python-simple') {
                // 基础 Python 缩进格式化（实验版，非 Black）：规则：冒号结尾 +1；以关键字 else/elif/except/finally/except 开头先 -1；行首去多余空格
                const indentSize = 4; // Python 习惯 4 空格
                const blockOpen = /:\s*(#.*)?$/; // 行尾冒号
                const dedentKeywords = /^(elif|else|except|finally)\b/;
                const lines = displayed.replace(/\t/g, '    ').split(/\r?\n/);
                let indent = 0;
                const out: string[] = [];
                let inTriple = false;
                const triplePattern = /'''|"""/g;
                for (let raw of lines) {
                    let line = raw.replace(/\s+$/, '');
                    const stripped = line.trim();
                    if (!stripped) { out.push(''); continue; }
                    // 处理三引号块不动缩进
                    const tripleMatches = stripped.match(triplePattern);
                    if (tripleMatches) {
                        // toggle per occurrence
                        tripleMatches.forEach(() => inTriple = !inTriple);
                    }
                    if (!inTriple) {
                        // 先判断是否 dedent
                        if (dedentKeywords.test(stripped) && indent > 0) {
                            indent--;
                        }
                        const currentIndent = ' '.repeat(indent * indentSize);
                        out.push(currentIndent + stripped);
                        if (blockOpen.test(stripped) && !/^(return|break|continue|pass)\b/.test(stripped)) {
                            indent++;
                        }
                    } else {
                        // 保留原缩进
                        out.push(line);
                    }
                }
                const result = out.join('\n').replace(/\n{3,}/g, '\n\n');
                setDisplayed(result.trimEnd());
            } else {
                const prettier = (await import('prettier/standalone')).default;
                const plugins: any[] = [];
                if (parser !== 'java') {
                    // estree 仅在 JS/TS/JSON 等解析器链路需要，java 插件不依赖
                    const estree = (await import('prettier/plugins/estree')).default;
                    plugins.push(estree);
                }
                if (parser === 'babel' || parser === 'json') {
                    const babel = (await import('prettier/plugins/babel')).default;
                    plugins.push(babel);
                }
                if (parser === 'typescript') {
                    const ts = (await import('prettier/plugins/typescript')).default;
                    const babel = (await import('prettier/plugins/babel')).default;
                    plugins.push(ts, babel);
                }
                if (parser === 'html') {
                    const html = (await import('prettier/plugins/html')).default;
                    plugins.push(html);
                }
                if (parser === 'css') {
                    const postcss = (await import('prettier/plugins/postcss')).default;
                    plugins.push(postcss);
                }
                if (parser === 'markdown') {
                    const md = (await import('prettier/plugins/markdown')).default;
                    plugins.push(md);
                }
                if (parser === 'yaml') {
                    const yaml = (await import('prettier/plugins/yaml')).default;
                    plugins.push(yaml);
                }
                if (parser === 'graphql') {
                    const gql = (await import('prettier/plugins/graphql')).default;
                    plugins.push(gql);
                }
                if (parser === 'java') {
                    const javaPlugin = (await import('prettier-plugin-java')).default;
                    plugins.push(javaPlugin);
                }
                const formatted = await prettier.format(displayed, {
                    parser,
                    plugins,
                    tabWidth: 2,
                    printWidth: 100,
                } as any);
                setDisplayed(formatted.trimEnd());
            }
        } catch (err: any) {
            message.error(`格式化失败：${err?.message || '未知错误'}`);
        } finally {
            setFormatting(false);
        }
    };

    const SyntaxHighlighter: any = SyntaxHighlighterBase as any;
    return (
        <div className="code-block-wrapper">
            <div className="code-actions">
                <button
                    className="format-button"
                    onClick={onFormat}
                    disabled={formatting || !parser}
                    title={parser ? (formatting ? '格式化中…' : (parser === 'c-like' ? '实验性 C/C++ 缩进格式化' : (parser === 'python-simple' ? '实验性 Python 缩进格式化' : '一键格式化'))) : '暂不支持该语言的前端格式化'}
                >
                    {formatting ? '格式化中…' : (parser === 'c-like' || parser === 'python-simple' ? '试验格式化' : '格式化')}
                </button>
                <button className="copy-button" onClick={onCopy} title="复制代码">
                    {copied ? '已复制' : '复制'}
                </button>
            </div>
            <SyntaxHighlighter
                language={language}
                style={settings.darkMode ? oneDark : oneLight}
                showLineNumbers
                customStyle={{
                    margin: '12px 0',
                    borderRadius: 8,
                    fontSize: 13,
                }}
            >
                {displayed}
            </SyntaxHighlighter>
        </div>
    );
};

export default CodeBlock;
